/**
 * Lite finished-meeting page — transcript + speakers + export. No summary,
 * chapters, or Ask AI. Mounted from the meeting-detail route when the meeting
 * is `meeting-lite` and no longer live/finalizing.
 */
import { recordingModeOf, type TExperienceMode } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useMediaQuery } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import type { QueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ConvertRecordingModeModal } from "../../../../components/ConvertRecordingModeModal"
import { MeetingAccessModal } from "../../../../components/MeetingAccessModal"
import { MeetingRecordingMismatchBanner } from "../../../../components/MeetingRecordingMismatchBanner"
import {
  MeetingPlayerProvider,
  useMeetingPlayerController,
} from "../../../../components/MeetingSummary/MeetingPlayerContext"
import { Player } from "../../../../components/Player"
import { SpeakerStatusBadges } from "../../../../components/SpeakerChip/SpeakerStatusBadges"
import { SpeakerEditModal } from "../../../../components/SpeakerEditModal"
import type { INamingPerson } from "../../../../components/SpeakerNamingStrip/interfaces"
import { WhoSpokePanel } from "../../../../components/WhoSpokePanel"
import { needsAName } from "../../../../components/WhoSpokePanel/helpers"
import { SpeakerColorsProvider } from "../../../../contexts/SpeakerColors"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import { useExperienceMode } from "../../../../hooks/useExperienceMode"
import { convertMeetingRecordingMode, fetchReprocessEstimate } from "../../../../lib/api"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import { notifyError } from "../../../../lib/notify"
import { resolveSpeakerColor } from "../../../../lib/speaker-colors"
import { normalizeSegmentSpeakers, speakerLabelDisplayMap } from "../../../../lib/speaker-mapping"
import {
  type ISpeakerVMResult,
  speakerDisplayLabel,
  speakerDisplayNameList,
  speakerEntityId,
} from "../../../../lib/speaker-vm"
import type { IMeetingDetail } from "../../../../lib/types/meeting"
import { TranscriptView } from "../../-transcript/TranscriptView"
import { useTranscriptPage } from "../../-transcript/useTranscriptPage"
import { copyTranscript } from "./copy-transcript"
import { MeetingDetailLiteView } from "./MeetingDetailLiteView"
import { useMeetingLifecycleActions } from "./useMeetingLifecycleActions"

export interface IMeetingDetailLiteProps {
  meeting: IMeetingDetail
  meetingId: string
  orgId: string | null
  company: CompanyApi | null
  companyId: string | undefined
  queryClient: QueryClient
  speakerVMs: ISpeakerVMResult
  speakerColorMap: Map<string, string>
  canManageAccess: boolean
  isOrgAdmin: boolean
  isOrganizer: boolean
  accessOpened: boolean
  openAccess: () => void
  closeAccess: () => void
  refetch: () => unknown
  navigateHome: () => void
  experienceMode?: TExperienceMode
}

export function MeetingDetailLite({
  meeting,
  meetingId,
  orgId,
  company,
  companyId,
  queryClient,
  speakerVMs,
  speakerColorMap,
  canManageAccess,
  isOrgAdmin,
  isOrganizer,
  accessOpened,
  openAccess,
  closeAccess,
  refetch,
  navigateHome,
  experienceMode = "full",
}: IMeetingDetailLiteProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [copyPending, setCopyPending] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradePending, setUpgradePending] = useState(false)
  const [upgradeCredits, setUpgradeCredits] = useState<number | null>(null)
  const [focusSignal, setFocusSignal] = useState(0)
  const focusTimer = useRef<number | null>(null)
  const mode = useExperienceMode(experienceMode)
  const recordedAs = recordingModeOf(meeting.template_id)
  const hasAnalysis = Boolean(meeting.analysis)
  const page = useTranscriptPage(meetingId, { enrichSegments: true })
  const { onRenameSubject, onToggleHidden, onDelete } = useMeetingLifecycleActions({
    company,
    meetingId,
    orgId,
    queryClient,
    refetch,
    navigateHome,
    isOrgAdmin,
    isOrganizer,
    hidden: meeting.hidden ?? false,
  })

  useEffect(() => {
    if (!upgradeOpen || hasAnalysis || !company) {
      setUpgradeCredits(null)
      return
    }
    let cancelled = false
    void fetchReprocessEstimate(company, meetingId, { mode: "full" })
      .then((r) => {
        if (!cancelled) setUpgradeCredits(r?.credits ?? null)
      })
      .catch(() => {
        if (!cancelled) setUpgradeCredits(0)
      })
    return (): void => {
      cancelled = true
    }
  }, [upgradeOpen, hasAnalysis, company, meetingId])

  const handleCopy = useCallback((): void => {
    if (!company) return
    setCopyPending(true)
    void copyTranscript(company, meetingId)
      .then(() => {
        notifications.show({ message: i18n._(msg`Transcript copied`), color: "success" })
      })
      .catch((err: unknown) => {
        notifyError(err, i18n._(msg`Couldn't copy the transcript`))
      })
      .finally(() => {
        setCopyPending(false)
      })
  }, [company, i18n, meetingId])

  const title = displayMeetingTitle(meeting.subject, meeting.topics, i18n, {
    lite: true,
    startedAt: meeting.start_time,
  })
  const speakers = page.speakerVMs.speakers.length > 0 ? page.speakerVMs.speakers : speakerVMs.speakers
  const hasUnnamedSpeakers = speakers.some(needsAName)
  const colors = page.speakerVMs.colorMap.size > 0 ? page.speakerVMs.colorMap : speakerColorMap
  const recordingUrl = page.meeting?.recording_url ?? meeting.recording_url
  const {
    controls: playerControls,
    playerRef,
    onPlayingChange,
    onProgress,
    onSpeedChange,
  } = useMeetingPlayerController(Boolean(recordingUrl))
  // Same breakpoint as the view. The name field is focused after the narrow
  // list has had a moment to open, so the focus isn't lost inside a closed collapse.
  const isNarrow = useMediaQuery("(max-width: 61.99em)", false, { getInitialValueInEffect: false })
  const unnamedSpeakers = useMemo(
    () =>
      speakers
        .filter(needsAName)
        .slice()
        .sort((a, b) => (b.talkPercent ?? 0) - (a.talkPercent ?? 0) || a.slot - b.slot),
    [speakers],
  )
  const namingPeople = useMemo(
    (): INamingPerson[] =>
      speakers
        .filter((vm) => !needsAName(vm))
        .map((vm) => {
          const name = speakerDisplayLabel(vm, i18n)
          return {
            key: speakerEntityId(vm) ?? vm.label ?? name,
            name,
            color: resolveSpeakerColor(name, colors),
            initials: vm.initials,
          }
        }),
    [colors, i18n, speakers],
  )
  const labelMap = useMemo(() => speakerLabelDisplayMap(speakers), [speakers])
  const speakerNames = useMemo(() => speakerDisplayNameList(speakers, i18n), [i18n, speakers])
  const playerSegments = useMemo(
    () => normalizeSegmentSpeakers(meeting.streaming_segments, labelMap.size > 0 ? labelMap : speakerNames),
    [labelMap, meeting.streaming_segments, speakerNames],
  )
  const firstUnnamed = unnamedSpeakers[0]

  useEffect(() => {
    return (): void => {
      if (focusTimer.current !== null) window.clearTimeout(focusTimer.current)
    }
  }, [])

  const handleNameClick = useCallback((): void => {
    if (focusTimer.current !== null) window.clearTimeout(focusTimer.current)
    if (isNarrow) {
      focusTimer.current = window.setTimeout(() => {
        focusTimer.current = null
        setFocusSignal((current) => current + 1)
      }, 240)
      return
    }
    setFocusSignal((current) => current + 1)
  }, [isNarrow])

  return (
    <SpeakerColorsProvider colors={colors}>
      <MeetingPlayerProvider value={playerControls}>
        <MeetingAccessModal
          opened={accessOpened}
          onClose={closeAccess}
          meetingId={meetingId}
          onChanged={() => refetch()}
        />
        <MeetingDetailLiteView
          title={title}
          onRename={onRenameSubject}
          startTime={meeting.start_time ?? null}
          durationMinutes={meeting.duration_minutes ?? 0}
          status={meeting.status}
          error={meeting.error}
          templateId={meeting.template_id}
          sourceLocale={meeting.analysis_locale ?? null}
          segments={meeting.streaming_segments}
          onCopyTranscript={handleCopy}
          copyPending={copyPending}
          meetingId={meetingId}
          companyId={companyId}
          canShare={canManageAccess}
          onManageAccess={canManageAccess ? openAccess : undefined}
          onToggleHidden={onToggleHidden}
          isHidden={meeting.hidden}
          onDelete={onDelete}
          onUpgradeToFull={canManageAccess ? () => setUpgradeOpen(true) : undefined}
          hasUnnamedSpeakers={hasUnnamedSpeakers}
          unnamedCount={unnamedSpeakers.length}
          firstUnnamedLabel={firstUnnamed ? speakerDisplayLabel(firstUnnamed, i18n) : undefined}
          firstUnnamedShare={firstUnnamed?.talkPercent != null ? firstUnnamed.talkPercent / 100 : undefined}
          namingPeople={namingPeople}
          onNameClick={handleNameClick}
          narrow={isNarrow}
          player={
            recordingUrl ? (
              <Player
                ref={playerRef}
                src={recordingUrl}
                segments={playerSegments}
                speakerColors={colors}
                speakerNames={speakerNames}
                speakers={speakers}
                showLegend={false}
                onPlayingChange={onPlayingChange}
                onProgress={onProgress}
                onSpeedChange={onSpeedChange}
              />
            ) : null
          }
          banner={
            recordedAs ? (
              <MeetingRecordingMismatchBanner
                recordedAs={recordedAs}
                defaultForNew={mode.current}
                onChangeDefault={mode.setMode}
                changing={mode.isPending}
              />
            ) : null
          }
          transcript={<TranscriptView {...page.viewProps} layout="embedded" speakersSlot="aside" />}
          speakers={
            <WhoSpokePanel
              meetingId={meetingId}
              speakers={speakers}
              focusFirstUnnamedSignal={focusSignal}
              onEditSpeaker={(speaker) => page.viewProps.onEditSpeaker(speaker.label ?? speaker.displayName)}
            />
          }
        />
        <SpeakerEditModal
          meetingId={meetingId}
          opened={page.editingSpeaker !== null}
          speaker={page.editingSpeaker}
          allSpeakers={page.speakerVMs.speakers}
          quotes={page.editingSpeaker ? (page.quotesMap.get(page.editingSpeaker.displayName) ?? []) : []}
          profile={page.editingProfile}
          speakerSegments={
            page.editingSpeaker?.label ? page.speakerSegmentsMap.get(page.editingSpeaker.label) : undefined
          }
          audioUrl={page.meeting?.recording_url ?? meeting.recording_url}
          titleBadges={
            page.editingSpeaker ? (
              <SpeakerStatusBadges
                status={page.editingSpeaker.status}
                recognitionSource={page.editingSpeaker.recognitionSource}
              />
            ) : null
          }
          onClose={page.closeSpeakerEdit}
          onEdited={page.onSpeakerEdited}
        />
        <ConvertRecordingModeModal
          opened={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          target="full"
          hasAnalysis={hasAnalysis}
          estimateCredits={upgradeCredits}
          loading={upgradePending}
          onConfirm={() => {
            if (!company) return
            setUpgradePending(true)
            void convertMeetingRecordingMode(company, meetingId, "full")
              .then((result) => {
                setUpgradeOpen(false)
                notifications.show({
                  message: result.processing
                    ? i18n._(msg`Processing this meeting into Full. Keep this page open.`)
                    : i18n._(msg`Full workspace restored.`),
                  color: "success",
                })
                void refetch()
              })
              .catch((err: unknown) => notifyError(err, i18n._(msg`Could not upgrade this meeting`)))
              .finally(() => setUpgradePending(false))
          }}
        />
      </MeetingPlayerProvider>
    </SpeakerColorsProvider>
  )
}
